import { DELEGATION_SCOPE, type AuthScope, type ScopeKey } from './types';

export const isPositiveInteger = (value: unknown): value is number =>
	typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

export const normalizeScope = (
	companyId: unknown,
	delegationIds: readonly unknown[] | null | undefined = [],
): AuthScope | null => {
	if (!isPositiveInteger(companyId)) return null;

	const normalizedDelegationIds = [
		...new Set((delegationIds ?? []).filter(isPositiveInteger)),
	].sort((left, right) => left - right);

	if (normalizedDelegationIds.length === 0) {
		return {
			companyId,
			delegationKind: DELEGATION_SCOPE.ALL,
			delegationIds: [],
		};
	}

	return {
		companyId,
		delegationKind: DELEGATION_SCOPE.SELECTED,
		delegationIds: normalizedDelegationIds,
	};
};

export const makeScopeKey = (scope: AuthScope): ScopeKey =>
	`v1:c=${scope.companyId};d=${
		scope.delegationKind === DELEGATION_SCOPE.ALL ? 'all' : scope.delegationIds.join(',')
	}`;

export const parseScopeKey = (value: unknown): AuthScope | null => {
	if (typeof value !== 'string') return null;

	const match = /^v1:c=(\d+);d=(all|\d+(?:,\d+)*)$/.exec(value);
	if (!match) return null;

	const companyId = Number(match[1]);
	const encodedDelegations = match[2];
	if (encodedDelegations === undefined) return null;
	const delegationIds =
		encodedDelegations === 'all' ? [] : encodedDelegations.split(',').map(Number);
	const scope = normalizeScope(companyId, delegationIds);

	if (scope === null || makeScopeKey(scope) !== value) return null;
	return scope;
};
